<template lang="pug">
  .color-kit-why-match.bg-gray-light(v-if="matchType && title" :class="{open: open}")
    template(v-if="matchType == 'customerMatch'")
      .why-title.no-why-content
        span.icon-check.xs-f-large.max-at-tweak
        p.xs-f-small.lg-f-medium.max-at-tweak You selected this color.
        button.btn-reset.color-purple.underline.xs-f-small.lg-f-medium.max-at-tweak(v-if="$root.colorMatches.bestMatch" @click="returnToBestMatch") Return to your best match

    template(v-else)
      button.why-title.btn-reset(type="button" @click="toggleOpen()")
        span.non-focusable(tabindex="-1")
          span.icon-check.xs-f-large.max-at-tweak
          span.title-text.semi-bold.xs-f-small.lg-f-medium.max-at-tweak {{title}}
          span.icon-caret-down.xs-f-large.max-at-tweak

      .why-content
        .why-content-inner
          template(v-if="matchType == 'bestMatch'")
            p.why-text.xs-f-small.lg-f-medium.max-at-tweak Based on your responses, this shade best addresses the following needs &amp; preferences:
            ul.xs-f-small.lg-f-medium.max-at-tweak
              li.xs-f-small.lg-f-medium.max-at-tweak(v-for="(reason, index) in bestMatchReasons" :key="index") {{reason}}
            p.xs-f-small.lg-f-medium.max-at-tweak Not what you had in mind? 
              button.btn-reset.color-purple.underline.xs-f-small.lg-f-medium.max-at-tweak(ref="tryItOn" @click="seeMoreColors(false, $refs.tryItOn)" :tabindex="open ? 0 : -1") Try on
              |  other colors or 
              button.btn-reset.color-purple.underline.xs-f-small.lg-f-medium.max-at-tweak(ref="more" @click="seeMoreColors(true, $refs.more)" :tabindex="open ? 0 : -1") see more colors
              | .

          template(v-if="matchType == 'mostPopular'")
            p.why-text.xs-f-small.lg-f-medium.max-at-tweak This shade is a popular choice for clients who also have your current hair color, and is most likely to give you the results you are looking for.
                    
          template(v-if="matchType == 'translatorMatch'")
            p.why-text.xs-f-small.lg-f-medium.max-at-tweak This shade most closely matches the color you're currently using at home.
            button.btn-reset.color-purple.xs-f-small.lg-f-medium.max-at-tweak(v-if="$root.colorMatches.bestMatch" @click="returnToBestMatch") Return to your best match.
          
          template(v-if="matchType == 'coloristMatch'")
            p.why-text.xs-f-small.lg-f-medium.max-at-tweak You spoke with a colorist who recommended this option for you.
            button.btn-reset.color-purple.xs-f-small.lg-f-medium.max-at-tweak(v-if="$root.colorMatches.bestMatch" @click="returnToBestMatch") Return to your best match.

</template>

<script>
  import { mapState } from 'vuex';

  export default {
    name: "ColorKitWhyMatch",

    props: {
      matchType: {
        type: String
      },
      product: {
        type: Object
      }
    },

    data() {
      return {
        open: false,
        contentMap: {
          bestMatch: {
            title: "Why is this my best match?"
          },
          mostPopular: {
            title: "Why is this my most popular option?"
          },
          customerMatch: {
            title: ""
          },
          translatorMatch: {},
          coloristMatch: {}
        }
      }
    },

    computed: {
      ...mapState('customer', ['cdata']),
      ...mapState('pdp', ['tones', 'toneMap']),

      advisorComplete() {
        return this.$root.advisorComplete;
      },

      answers() {
        return this.$root.advisorAnswers;
      },

      colorMatches() {
        return this.$root.colorMatches;
      },

      title() {
        if (this.matchType == "bestMatch") {
          return "Why is this my best match?";
        } else if (this.matchType == "mostPopular") {
          return "Why is this my most popular option?";
        } else if (this.matchType == "customerMatch") {
          return "Why is this my choice?";
        } else if (this.matchType == "translatorMatch") {
          return "Why is this the translator match?";
        } else if (this.matchType == "coloristMatch") {
          return "Why is this my colorist's choice?";
        }
      },

      bestMatchReasons() {
        if (!this.cdata || !this.answers) {
          return [];
        }
        var reasons = [];

        if (this.productTones[this.answers.hair_tone]) {
          reasons.push(this.toneMap[this.answers.hair_tone] + " tones");
        }

        if (this.product.highGrayCoverage) {
          reasons.push("Superior gray coverage");
        }

        if (this.answers.treated) {
          if (this.productLevel < this.answers.hair_shade) {
            reasons.push("Darker than your color-treated hair shade");
          }

          if (this.productLevel > this.answers.hair_shade) {
            reasons.push("Lighter than your color-treated hair shade");
          }

          if (this.productLevel == this.answers.hair_shade) {
            reasons.push("Same as your color-treated hair shade");
          }
        } else {
          if (this.productLevel < this.answers.natural_hair_shade) {
            reasons.push("Darker than your natural hair shade");
          }

          if (this.productLevel > this.answers.natural_hair_shade) {
            reasons.push("Lighter than your natural hair shade");
          }

          if (this.productLevel == this.answers.natural_hair_shade) {
            reasons.push("Same as your natural hair shade");
          }
        }

        return reasons;
      },

      productTones() {
        var prodTones = {};
        
        this.product.code.split('').forEach(letter => {
          if (this.tones[letter]) {
            prodTones[this.tones[letter]] = true;
          }
        });

        return prodTones;
      },

      productLevel() {
        return +this.product.code.match(/\d+/g)[0];
      }
    },

    methods: {
      toggleOpen() {
        this.open = !this.open;
      },

      returnToBestMatch() {
        this.$router.push('/product/' + this.colorMatches.bestMatch.url_key);
      }
    },

    watch: {
      matchType() {
        this.open = false;
      }
    }
  }
</script>

<style scoped lang="stylus">
  .color-kit-why-match
    overflow hidden
    margin-top 0.75em
    padding 0.5em
    
    .why-title
      position relative
      width 100%
      
      .non-focusable
        padding 1em
        padding-left 2.75em
      
      .icon-check
        position absolute
        top 50%
        margin-top -0.7em
        left 0.3em
        
      .title-text
        display block
        width 16em
      
      .icon-caret-down
        position absolute
        top 0.6em
        right 0.25em
        transition(transform 350ms ease)
    
    .why-content
      overflow hidden
      max-height 0
      transition(max-height 400ms ease-in-out)
      
      .why-content-inner
        padding 1.5em
        padding-top 0
        padding-left 3.25em
        padding-bottom 2em
        
        .why-text
          padding-bottom 0.25em
        
        ul
          list-style disc
          padding-left 1em
          padding-top 0.25em
          padding-bottom 0.5em
          
    .no-why-content
      padding-left 3.25em
      
    &.open
      .why-title
        .icon-caret-down
          transform(rotate(-180deg))
          
      .why-content
        max-height 30em
        
    @media mq-desktop-md
      .why-title
        .title-text
          width 14em
    
</style>
